import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { History, Link2, Upload } from 'lucide-react';
import epcProjectService from '../../../services/epcProjectService';

type ImportRow = {
  _id?: string;
  id?: string;
  version?: number;
  source?: string;
  sourceUrl?: string;
  originalFileName?: string;
  status?: string;
  errorMessage?: string;
  taskCount?: number;
  createdAt?: string;
};

function asImportRow(row: unknown): ImportRow {
  if (row && typeof row === 'object') return row as ImportRow;
  return {};
}

type Props = {
  projectId: string;
  canImport: boolean;
  urlInput: string;
  setUrlInput: (v: string) => void;
  mppBusy: boolean;
  onUrlImport: () => void;
  importHistory: unknown[];
  onImported: () => Promise<void>;
};

export default function EpcMppImportsPanel({
  projectId,
  canImport,
  urlInput,
  setUrlInput,
  mppBusy,
  onUrlImport,
  importHistory,
  onImported,
}: Props) {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.mpp') && !lowerName.endsWith('.xml')) {
      toast.error('Only .mpp and .xml (MS Project XML) files are supported');
      e.target.value = '';
      return;
    }
    if (!canImport) {
      toast.error('You do not have permission to import');
      e.target.value = '';
      return;
    }
    setUploadBusy(true);
    try {
      const result = await epcProjectService.importMpp(projectId, file);
      if (result.staged) {
        const sid = result.import?.id ?? result.import?._id;
        toast('Schedule staged — review changes before applying');
        navigate(`/epc/projects/${projectId}/schedule/compare/${sid}`);
      } else {
        toast.success('Schedule imported');
        await onImported();
      }
    } catch (err: unknown) {
      let msg = 'Schedule upload failed';
      if (axios.isAxiosError(err)) {
        const d = err.response?.data as { message?: string; detail?: string } | undefined;
        msg = d?.detail || d?.message || err.message || msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      toast.error(msg);
    } finally {
      setUploadBusy(false);
      e.target.value = '';
    }
  };

  const busy = mppBusy || uploadBusy;

  return (
    <div className="space-y-8 max-w-3xl font-[family-name:var(--font-outfit)]">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-4">
          <Upload className="h-4 w-4 text-emerald-600" strokeWidth={2} />
          Upload MS Project file (.mpp) or MS Project XML (.xml)
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".mpp,.xml,application/vnd.ms-project,text/xml,application/xml"
          className="hidden"
          onChange={(ev) => void onFileChange(ev)}
          disabled={!canImport || busy}
        />
        <button
          type="button"
          disabled={!canImport || busy || !projectId}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploadBusy ? 'Uploading…' : 'Choose file'}
        </button>
        {!canImport ? (
          <p className="mt-2 text-xs text-amber-800/90">Import permission is required to upload schedules.</p>
        ) : null}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-4">
          <Link2 className="h-4 w-4 text-emerald-600" strokeWidth={2} />
          Import from URL
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://…"
            disabled={!canImport || busy}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/15 outline-none disabled:opacity-50"
          />
          <button
            type="button"
            disabled={!canImport || busy || !urlInput.trim() || !projectId}
            onClick={onUrlImport}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #339274, #29795f)' }}
          >
            {mppBusy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-4">
          <History className="h-4 w-4 text-slate-500" strokeWidth={2} />
          Import history
        </div>
        {importHistory.length === 0 ? (
          <p className="text-sm text-slate-500">No imports yet for this project.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
            {importHistory.map((raw, i) => {
              const h = asImportRow(raw);
              const key = h._id ?? h.id ?? `imp-${i}`;
              const label =
                h.originalFileName ||
                (h.source === 'url' && h.sourceUrl ? h.sourceUrl : null) ||
                `${h.source ?? 'import'} · v${h.version ?? '—'}`;
              return (
                <li key={key} className="px-4 py-3 text-sm bg-white hover:bg-slate-50/80">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-slate-800 truncate max-w-[min(100%,28rem)]" title={label}>
                      {label}
                    </span>
                    <span
                      className={`text-xs font-semibold uppercase ${
                        h.status === 'failed'
                          ? 'text-red-600'
                          : h.status === 'ready'
                            ? 'text-emerald-700'
                            : 'text-slate-500'
                      }`}
                    >
                      {h.status ?? '—'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                    {h.version != null ? <span>Version {h.version}</span> : null}
                    {h.source ? <span className="capitalize">{h.source}</span> : null}
                    {h.taskCount != null ? <span>{h.taskCount} tasks</span> : null}
                    {h.createdAt ? <span>{new Date(h.createdAt).toLocaleString()}</span> : null}
                  </div>
                  {h.status === 'failed' && h.errorMessage ? (
                    <p className="mt-2 text-xs text-red-700 bg-red-50/80 rounded px-2 py-1.5">{h.errorMessage}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
